import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaTrash, FaEdit, FaSignOutAlt, FaSun, FaMoon, FaHome, FaTimes } from 'react-icons/fa';
import { Mail, MapPin, ShieldCheck } from 'lucide-react';
import { getCSRFToken } from '../../utils/auth';
import MyListingsDashboard from '../../Components/PropertyCard/MyListingsDashboard';
import { useTranslation } from 'react-i18next';
import api from '../../api/axiosConfig';



const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://pongo-orvz.onrender.com/pongo';

const getInitials = (user) => {
  if (user?.first_name && user?.last_name) {
    return (user.first_name[0] + user.last_name[0]).toUpperCase();
  }
  return user?.username ? user.username[0].toUpperCase() : '?';
};



const uploadProfileImageToGitHub = async (file, user, API_BASE_URL) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('filename', `profile-${user.id}-${Date.now()}.jpg`);

  try {
    const csrfToken = await getCSRFToken();
    const response = await fetch(`${API_BASE_URL}/upload-avatar/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRFToken': csrfToken,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to upload profile image');
    }

    const result = await response.json();
    return result.image_url;
  } catch (err) {
    console.error('Profile image upload failed:', err);
    throw new Error(err.message || 'Failed to upload profile photo. Please try again.');
  }
};

export default function ProfileDash() {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [user, setUser] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteAvatarConfirm, setShowDeleteAvatarConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const fileInputRef = useRef(null);
  const [showListings, setShowListings] = useState(false);
  

  // Settings state
  const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';

  const { t } = useTranslation('Profiledash');
  const { t: tCommon } = useTranslation('translation');

  const [phone, setPhone] = useState('');
  const [isSaving, setIsSaving] = useState(false);




  const toggleTheme = () => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };


  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const csrfToken = await getCSRFToken();
        if (!csrfToken) {
          setError(t('profile.errors.authentication'));
          return;
        }

        const response = await fetch(`${API_BASE_URL}/get_user_profile/`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'X-CSRFToken': csrfToken,
            'Accept': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.clear();
            navigate('/login');
            return;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        setUser(data);
        setAvatarUrl(data.avatar_url);
      } catch (err) {
        console.error('Profile fetch error:', err);
        setError(t('profile.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate, t]);


  
  useEffect(() => {
    if (user) {
      setPhone(user.phone_number || '');
    }
  }, [user]);


  const handleSavePhone = async () => {
    setIsSaving(true);
    try {
      //  Use PATCH, not POST
      const response = await api.patch(
        '/profile-view/', // or full URL if needed
        { phone_number: phone },
        { withCredentials: true }
      );
      setUser(prev => ({ ...prev, phone_number: response.data.phone_number }));
      
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      const csrfToken = await getCSRFToken();
      if (!csrfToken) {
        localStorage.clear();
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/logout_view/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken },
      });

      if (response.ok) {
        localStorage.clear();
        navigate('/login');
      }
    } catch (err) {
      console.error('Logout error:', err);
      localStorage.clear();
      navigate('/login');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const csrfToken = await getCSRFToken();
      if (!csrfToken) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/delete-account/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      });

      if (response.ok) {
        setShowDeleteConfirm(false);
        localStorage.clear();
        navigate('/login');
      } else {
        alert('Failed to delete account. Please try again.');
      }
    } catch (err) {
      console.error('Delete account error:', err);
      alert('An error occurred.');
    }
  };

  const handleImageChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  // Client-side validation
  if (!file.type.startsWith('image/')) {
    alert('Please select a valid image file');
    return;
  }

  if (file.size > 5 * 1024 * 1024) { 
    alert('Image must be under 5MB');
    return;
  }

  setIsUploading(true);
  const previewUrl = URL.createObjectURL(file);
  setAvatarUrl(previewUrl);

  try {
    const imageUrl = await uploadProfileImageToGitHub(file, user, API_BASE_URL);
    setAvatarUrl(imageUrl);    
    setUser(prev => prev ? { ...prev, avatar_url: imageUrl } : null);
    
    URL.revokeObjectURL(previewUrl);
  } catch (err) {
    console.error('Upload failed:', err);
    alert(`Failed to upload profile photo: ${err.message || 'Please try again'}`);
    setAvatarUrl(user?.avatar_url || user?.avatar || null);
    URL.revokeObjectURL(previewUrl);
  
  } finally {
    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }
};
  const handleDeleteAvatar = async () => {
    try {
      const csrfToken = await getCSRFToken();
      const response = await fetch(`${API_BASE_URL}/delete-avatar/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setAvatarUrl(null);
        // Update user state to reflect the change
        setUser(prev => prev ? { ...prev, avatar_url: null } : null);
      } else {
        const err = await response.json();
        alert(`Failed to delete photo: ${err.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Delete avatar error:', err);
      alert('Failed to delete photo. Please try again.');
    } finally {
      setShowDeleteAvatarConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-black)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-[var(--bg-black)] flex items-center justify-center text-red-400">
        {error || t('profile.errors.loadFailed')}
      </div>
    );
  }




  const initials = getInitials(user);

  return (
    <div className="min-h-screen bg-[var(--bg-black)] text-[var(--text-black)] pb-20">
      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Profile Card */}
        <div className="bg-[var(--bg-black)] border border-[var(--border)] rounded-xl p-6 text-center">
          <div className="relative inline-block mb-4">
            {avatarUrl ? (
              <div className="relative">
                <img
                  src={avatarUrl}
                  alt={user.username}
                  className="w-24 h-24 rounded-full ring-4 ring-pink-500 object-cover mx-auto"
                />
                <button
                  onClick={() => setShowDeleteAvatarConfirm(true)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition border-none"
                  aria-label="Delete profile photo"
                >
                  <FaTimes size={10} />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-full ring-4 ring-pink-500 bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold mx-auto">
                {initials}
              </div>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className={`absolute bottom-0 right-0 ${isUploading ? 'bg-gray-400' : 'bg-pink-500'} text-white p-1.5 border-none rounded-full hover:bg-pink-600 transition`}
              aria-label="Change profile picture"
            >
              {isUploading ? (
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
              ) : (
                <FaEdit size={12} />
              )}
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/*"
              className="hidden"
            />
          </div>
          <h2 className="text-xl font-bold">{user.username}</h2>
          <p className="text-gray-400 text-sm mt-1 flex items-center justify-center gap-1">
            <Mail size={14} /> {user.email}
          </p>
          <div className="flex items-center justify-center gap-1 text-xs text-gray-400 mt-2">
            <MapPin size={12} /> <span>{user.location}</span>
          </div>
          {user.isVerified && (
            <div className="mt-2 inline-flex items-center gap-1 bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs">
              <ShieldCheck size={12} /> {t('profile.user.verified')}
            </div>
          )}
          <p className="text-gray-500 text-xs mt-3"> {t('profile.user.memberSince', { date: user.memberSince })} </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          {/* My Listings */}
          <button
            onClick={() => setShowListings(true)}
            className="w-full flex items-center justify-between p-4 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl transition"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <FaHome className="text-green-400" size={20} />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-[var(--text-black)]">{t('profile.actions.myListings.title')}</h3>
                <p className="text-sm text-gray-400">{t('profile.actions.myListings.description')}</p>
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Settings */}
          <button
            onClick={() => setShowSettings(true)} 
            className="w-full flex items-center justify-between p-4 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl transition"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <FaCog className="text-blue-400" size={20} />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-[var(--text-black)]">{t('profile.actions.settings.title')}</h3>
                <p className="text-sm text-gray-400">{t('profile.actions.settings.description')}</p>
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Logout */}
          <button
            onClick={() => setShowLogoutConfirm(true)}
            className="w-full flex items-center justify-between p-4 bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl transition"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <FaSignOutAlt className="text-orange-400" size={20} />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-[var(--text-black)]">{t('profile.actions.logout.title')}</h3>
                <p className="text-sm text-gray-400">{t('profile.actions.logout.description')}</p>
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Delete Account */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full flex items-center justify-between p-4 bg-[var(--bg-black)] border border-pink-500/30 border-solid rounded-xl hover:bg-red-500/10 transition"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <FaTrash className="text-red-400" size={20} />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-pink-500">{t('profile.actions.deleteAccount.title')}</h3>
                <p className="text-sm text-gray-400">{t('profile.actions.deleteAccount.description')}</p>
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {/* Phone Number Section */}
        <div className="p-4 bg-white/5 rounded-xl border border-[var(--border)]">
          <label className="text-sm font-semibold mb-2 text-[var(--text-black)] flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.552 21 3 14.448 3 6.5V5z" />
            </svg>
            {tCommon('profile.phone.label')}
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            placeholder={tCommon('profile.phone.placeholder')}
            className="w-full px-4 py-2.5 rounded-lg bg-[var(--bg-black)] border border-[var(--border)] text-[var(--text-black)] focus:outline-none focus:ring-2 focus:ring-pink-500"
          />
          <p className="text-xs text-gray-400 mt-1">
            {tCommon('profile.phone.hint')}
          </p>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-pink-500">{t('profile.settings.title')}</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-pink-500 bg-[var(--bg-black)] font-bold border-none"
              >
                ✕
              </button>
            </div>

            {/* Theme */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">{t('profile.settings.appearance')}</h4>
              <button
                onClick={toggleTheme}
                className="flex items-center gap-3 p-3 rounded-lg border border-[var(--border)] text-[var(--text-black)] border-solid bg-[var(--bg-black)] w-full text-left"
              >
                {currentTheme === 'light' ? (
                  <>
                    <FaSun className="text-yellow-400" /> {t('profile.settings.theme.dark')}
                  </>
                ) : (
                  <>
                    <FaMoon className="text-blue-300" /> {t('profile.settings.theme.light')}
                  </>
                )}
              </button>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-medium mb-2">Legal Security"</h4>
              <button
                onClick={() => {
                  navigate('/terms-and-privacy');
                  setShowSettings(false);
                }}
                className="w-full text-left p-3 rounded-lg border border-[var(--border)] bg-[var(--bg-black)] text-[var(--text-black)] border-solid transition"
              >
                Terms and Privacy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-pink-500 mb-2">{t('profile.deleteConfirm.title')}</h3>
            <p className="text-gray-400 mb-4">
              {t('profile.deleteConfirm.description')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 rounded-lg border bg-[var(--bg-black)] border-[var(--border)] border-solid text-[var(--text-black)]"
              >
                {t('profile.deleteConfirm.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                className="flex-1 py-2.5 bg-red-500 border-none text-white rounded-lg hover:bg-pink-600 transition"
              >
                {t('profile.deleteConfirm.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Avatar Confirmation Modal */}
      {showDeleteAvatarConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-pink-500 mb-2">{t('profile.deleteConfirm.title')}</h3>
            <p className="text-gray-400 mb-4">
              {t('profile.deleteConfirm.description')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteAvatarConfirm(false)}
                className="flex-1 py-2.5 rounded-lg border bg-[var(--bg-black)] border-[var(--border)] border-solid text-[var(--text-black)]"
              >
                {t('profile.deleteConfirm.cancel')}
              </button>
              <button
                onClick={handleDeleteAvatar}
                className="flex-1 py-2.5 bg-red-500 border-none text-white rounded-lg hover:bg-pink-600 transition"
              >
                {t('profile.deleteConfirm.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-black)] border border-[var(--border)] border-solid rounded-xl w-full max-w-md p-6">
            <div className="flex gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="flex-1 py-2.5 rounded-lg border bg-[var(--bg-black)] border-[var(--border)] border-solid text-[var(--text-black)]"
              >
                {t('profile.deleteConfirm.cancel')}
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-2.5 bg-red-500 border-none text-white rounded-lg hover:bg-pink-600 transition"
              >
                {t('profile.actions.logout.title')}
              </button>
            </div>
          </div>
        </div>
      )}




      <div className="flex justify-center">
        <button
          onClick={handleSavePhone}
          disabled={isSaving}
          className="mt-3 py-2.5 px-6 bg-pink-500 text-white rounded-lg font-semibold border-none w-64 hover:bg-pink-600 focus:outline-none focus:ring-2 focus:ring-pink-300 transition-all duration-300 ease-in-out disabled:bg-gray-400 disabled:cursor-not-allowed"
          aria-label={isSaving ? tCommon('profile.phone.saving') : tCommon('profile.phone.save')}
        >
          {isSaving ? tCommon('profile.phone.saving') : tCommon('profile.phone.save')}
        </button>
      </div>


      {/* My Listings Dashboard */}
      {showListings && (
        <MyListingsDashboard
          onClose={() => setShowListings(false)}
          onAdd={() => {
            setShowListings(false);
            navigate('/uploaddash'); 
          }}
        />
      )}
    </div>
  );
}